import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building, ChevronLeft, ChevronRight } from 'lucide-react';
import type { TravelAgency } from '../types';
import NearbyAgenciesModal from './NearbyAgenciesModal';
import ImageWithFallback from './ImageWithFallback';
import AirlineBadge from './AirlineBadge';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { parseFlexibleDate } from '../utils/dateParse';
import { getCategoryLabel } from '../utils/categoryLabel';

export interface OffersPackageData {
  id: number;
  title: string;
  description: string;
  image: string;
  groupSize: string;
  category: string;
  type: 'B2B' | 'B2C';
  departure: string;
  departures: {
    id: number;
    departureDate: string;
    returnDate: string;
  }[];
  formulas: {
    id: number;
    name: string;
    priceType: string;
    perPersonPrice: number | null;
    roomPrices: {
      single: number;
      double: number;
      triple: number;
      quadruple: number;
      quintuple: number;
    };
  }[];
  agency?: {
    id: number;
    name: string;
    logo: string;
    rating?: number;
    verified: boolean;
    secondaryColor?: string | null;
  };
  airline?: {
    id: number;
    name: string;
    logo: string;
    code?: string | null;
  } | null;
}

interface OffersPackageCardProps {
  package: OffersPackageData;
}

function getDisplayPrice(formula: OffersPackageData['formulas'][0]) {
  if (formula.priceType === 'PER_PERSON' && formula.perPersonPrice) {
    return formula.perPersonPrice;
  }
  if (formula.roomPrices) {
    const prices = Object.values(formula.roomPrices).filter((p) => p > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  }
  return 0;
}

export default function OffersPackageCard({ package: pkg }: OffersPackageCardProps) {
  const [showAgenciesModal, setShowAgenciesModal] = useState(false);
  const departures = Array.isArray(pkg.departures) ? pkg.departures : [];

  const lowestPrice =
    Array.isArray(pkg.formulas) && pkg.formulas.length > 0
      ? pkg.formulas.reduce((min, f) => Math.min(min, getDisplayPrice(f)), Infinity)
      : 0;

  const firstFormula = pkg.formulas?.[0];
  const perPerson =
    firstFormula?.priceType === 'PER_PERSON' && firstFormula.perPersonPrice != null;

  const getNextDeparture = () => {
    if (departures.length === 0) return null;
    const now = new Date();
    const future = departures
      .filter((d) => {
        const dt = parseFlexibleDate(d.departureDate);
        return !isNaN(dt.getTime()) && dt >= now;
      })
      .sort(
        (a, b) =>
          parseFlexibleDate(a.departureDate).getTime() -
          parseFlexibleDate(b.departureDate).getTime()
      );
    return future[0] || departures[0];
  };

  const nextDeparture = getNextDeparture();

  const getDuration = () => {
    if (!nextDeparture) return { days: 0, nights: 0 };
    try {
      const a = parseFlexibleDate(nextDeparture.departureDate);
      const b = parseFlexibleDate(nextDeparture.returnDate);
      if (isNaN(a.getTime()) || isNaN(b.getTime())) return { days: 0, nights: 0 };
      const days = differenceInDays(b, a);
      return { days: Math.max(days, 0), nights: Math.max(days - 1, 0) };
    } catch {
      return { days: 0, nights: 0 };
    }
  };

  const { days: daysCount, nights: nightsCount } = getDuration();

  const isValidDate = (date: string | number | Date | null | undefined): boolean => {
    if (date == null || date === '') return false;
    const d = parseFlexibleDate(date);
    return !isNaN(d.getTime());
  };

  const formatDateCompact = (date: string | number | Date | null | undefined): string => {
    if (!isValidDate(date)) return '—';
    try {
      return format(parseFlexibleDate(date), 'EEE d MMM', { locale: fr });
    } catch {
      return '—';
    }
  };

  const durationLabel = (() => {
    if (departures.length === 0) return 'Aucune date';
    if (
      nextDeparture &&
      isValidDate(nextDeparture.departureDate) &&
      isValidDate(nextDeparture.returnDate)
    ) {
      return `${daysCount} j · ${nightsCount} nuits`;
    }
    return 'Durée à confirmer';
  })();

  const categoryLabel = getCategoryLabel(pkg.category);
  const departureCity = pkg.departure?.trim() ?? '';

  const footerBits = [durationLabel, pkg.airline?.name].filter(Boolean);

  return (
    <>
      <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
        <div className="flex flex-col lg:flex-row lg:min-h-[220px]">
          {/* Image + faux carousel */}
          <div className="relative h-52 w-full shrink-0 overflow-hidden lg:h-auto lg:w-[30%] lg:min-h-[220px]">
            <ImageWithFallback
              src={pkg.image}
              alt={pkg.title}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center gap-1">
              <span className="h-1.5 w-4 rounded-full bg-white shadow" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/50" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/50" />
            </div>
            <button
              type="button"
              className="absolute left-2 top-1/2 z-[1] -translate-y-1/2 rounded-full bg-white/90 p-1 text-gray-800 shadow-md transition hover:bg-white"
              aria-label="Image précédente"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="absolute right-2 top-1/2 z-[1] -translate-y-1/2 rounded-full bg-white/90 p-1 text-gray-800 shadow-md transition hover:bg-white"
              aria-label="Image suivante"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Infos centrales */}
          <div className="flex min-w-0 flex-1 flex-col justify-between border-gray-200 p-4 lg:w-[45%] lg:border-l lg:border-t-0 lg:py-5 border-t">
            <div>
              <h2 className="text-lg font-bold leading-tight text-gray-900 sm:text-xl">
                {pkg.title}
              </h2>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                {categoryLabel && categoryLabel !== '—' && (
                  <span className="text-gray-600">{categoryLabel}</span>
                )}
                {departureCity && (
                  <span className="inline-flex items-center rounded-md bg-[#003580]/10 px-2 py-0.5 font-semibold text-[#003580] ring-1 ring-[#003580]/15">
                    Départ&nbsp;: {departureCity}
                  </span>
                )}
              </p>
              {nextDeparture && (
                <>
                  <div className="mt-5 h-px bg-gray-200" />
                  <div className="mt-3 flex flex-wrap items-end gap-6 text-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Date de départ
                      </p>
                      <p className="mt-1 font-semibold text-gray-900">
                        {formatDateCompact(nextDeparture.departureDate)}
                      </p>
                    </div>
                    {pkg.airline ? (
                      <AirlineBadge airline={pkg.airline} variant="labeled" />
                    ) : null}
                  </div>
                </>
              )}
              {pkg.description?.trim() ? (
                <p
                  className={`text-sm leading-relaxed text-gray-600 line-clamp-4 ${
                    nextDeparture ? 'mt-3' : 'mt-5'
                  }`}
                >
                  {pkg.description.trim()}
                </p>
              ) : null}
            </div>
          </div>

          {/* Prix + agence + CTA */}
          <div className="flex flex-col justify-between border-gray-200 p-4 lg:w-[25%] lg:border-l lg:border-t-0 border-t bg-gray-50/80 lg:bg-white">
            <div>
              {firstFormula?.name && (
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {firstFormula.name}
                </p>
              )}
              <p className="mt-1 text-3xl font-bold tabular-nums text-gray-900">
                {lowestPrice.toLocaleString('fr-DZ')}{' '}
                <span className="text-lg font-semibold text-gray-700">DA</span>
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {perPerson ? 'par personne' : 'à partir de'}
              </p>
            </div>

            {pkg.agency && (
              <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-4">
                {pkg.agency.logo ? (
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg p-1"
                    style={{
                      backgroundColor: pkg.agency.secondaryColor?.trim() || '#1e3a8a',
                    }}
                  >
                    <ImageWithFallback
                      src={pkg.agency.logo}
                      alt=""
                      className="h-full w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-900 text-white">
                    <Building className="h-5 w-5" />
                  </div>
                )}
                <span className="truncate text-sm font-medium text-gray-800">{pkg.agency.name}</span>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2">
              <Link
                to={`/package/${pkg.id}`}
                className="block w-full rounded-lg bg-[#003580] py-3 text-center text-sm font-semibold text-white transition hover:bg-[#00224d]"
              >
                Voir le séjour
              </Link>
              <button
                type="button"
                onClick={() => setShowAgenciesModal(true)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Agences à proximité
              </button>
            </div>
          </div>
        </div>

        {/* Bandeau bas type comparateur */}
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-600">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            {footerBits.map((bit, i) => (
              <span key={i} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-gray-300">·</span> : null}
                <span>{bit}</span>
              </span>
            ))}
          </div>
        </div>
      </article>

      <NearbyAgenciesModal
        isOpen={showAgenciesModal}
        onClose={() => setShowAgenciesModal(false)}
        agencies={
          pkg.agency
            ? ([
                {
                  id: String(pkg.agency.id),
                  name: pkg.agency.name,
                  logo: pkg.agency.logo,
                  rating: pkg.agency.rating ?? 0,
                  reviewCount: 0,
                  verified: pkg.agency.verified,
                },
              ] satisfies TravelAgency[])
            : []
        }
      />
    </>
  );
}
